import React, { useState } from 'react';
import { Send, Search, Coins } from 'lucide-react';
import axios from 'axios';

const SendPointsTab = ({
  user,
  loading,
  setLoading,
  language,
  t,
  showSuccessToast,
  showErrorToast,
  loadData,
  onUserUpdate
}) => {
  // Send points state
  const [sendPoints, setSendPoints] = useState({
    searchTerm: '',
    searchResults: [],
    selectedUser: null,
    isSearching: false,
    pointsAmount: '',
    reason: '',
    showConfirmModal: false
  });

  // Search users function
  const searchUsersForSend = async (searchTerm) => {
    if (!searchTerm.trim() || searchTerm.length < 2) {
      setSendPoints(prev => ({ ...prev, searchResults: [], isSearching: false }));
      return;
    }

    setSendPoints(prev => ({ ...prev, isSearching: true }));

    try {
      const response = await axios.get(`/api/users/search?q=${searchTerm}`);
      // Filter out current user from results
      const filteredResults = response.data.filter(u => u._id !== user._id);
      setSendPoints(prev => ({
        ...prev,
        searchResults: filteredResults,
        isSearching: false
      }));
    } catch (error) {
      console.error('Search users error:', error);
      setSendPoints(prev => ({ ...prev, searchResults: [], isSearching: false }));
      showErrorToast(t('failedToSearchUsers', language) || 'Failed to search users');
    }
  };

  // Handle send points
  const handleSendPoints = async () => {
    const { selectedUser, pointsAmount, reason } = sendPoints;

    if (!selectedUser || !pointsAmount || isNaN(pointsAmount) || parseInt(pointsAmount) <= 0) {
      showErrorToast(t('pleaseEnterValidAmount', language) || 'Please enter a valid amount');
      return;
    }

    const points = parseInt(pointsAmount);

    if (points > user.points) {
      showErrorToast(t('insufficientPoints', language) || `Insufficient points. You have ${user.points} points.`);
      return;
    }

    try {
      setLoading(true);

      await axios.post('/api/users/transfer-points', {
        userId: selectedUser._id,
        points,
        reason
      });

      showSuccessToast(
        t('pointsSentSuccessfully', language, { points, username: selectedUser.inGameName }) ||
        `Successfully sent ${points} points to ${selectedUser.inGameName}`
      );

      // Reset form
      setSendPoints({
        searchTerm: '',
        searchResults: [],
        selectedUser: null,
        isSearching: false,
        pointsAmount: '',
        reason: '',
        showConfirmModal: false
      });

      // Reload data to update user's points
      loadData();
      onUserUpdate();

    } catch (error) {
      const message = error.response?.data?.message || t('failedToSendPoints', language) || 'Failed to send points';
      showErrorToast(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const searchTerm = e.target.value;
    setSendPoints(prev => ({ ...prev, searchTerm }));
    searchUsersForSend(searchTerm);
  };

  const selectUser = (selectedUser) => {
    setSendPoints(prev => ({
      ...prev,
      selectedUser,
      searchResults: [],
      searchTerm: selectedUser.inGameName
    }));
  };

  const openConfirmModal = () => {
    if (!sendPoints.selectedUser) {
      showErrorToast(t('pleaseSelectUser', language) || 'Please select a user');
      return;
    }
    if (!sendPoints.pointsAmount || isNaN(sendPoints.pointsAmount) || parseInt(sendPoints.pointsAmount) <= 0) {
      showErrorToast(t('pleaseEnterValidAmount', language) || 'Please enter a valid amount');
      return;
    }
    if (parseInt(sendPoints.pointsAmount) > user.points) {
      showErrorToast(t('insufficientPoints', language) || `Insufficient points. You have ${user.points} points.`);
      return;
    }
    setSendPoints(prev => ({ ...prev, showConfirmModal: true }));
  };

  const newBalance = user.points - (parseInt(sendPoints.pointsAmount) || 0);

  return (
    <div className="role-management">
      <div className="role-management-section">
        <h4 className="role-management-title">
          <Send className="w-5 h-5" />
          {t('sendPoints', language) || 'Send Points'}
        </h4>
        <p className="role-management-description">
          {t('sendPointsFromWallet', language) || 'Send points from your wallet to another user'}
        </p>

        {/* Your Balance Display */}
        <div className="your-balance-card" style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px',
          color: 'white'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <p style={{ fontSize: '14px', opacity: 0.9 }}>{t('yourBalance', language) || 'Your Balance'}</p>
              <p style={{ fontSize: '28px', fontWeight: 'bold' }}>{user.points.toLocaleString()} {t('points', language) || 'Points'}</p>
            </div>
            <Coins className="w-10 h-10" style={{ opacity: 0.8 }} />
          </div>
        </div>

        <div className="role-management-grid">
          {/* Search Section */}
          <div className="role-search-section">
            <div>
              <label className="role-search-label">
                {t('searchRecipient', language) || 'Search Recipient'}
              </label>
              <div className="role-search-container">
                <Search className="role-search-icon" />
                <input
                  type="text"
                  placeholder={t('searchByUsername', language) || 'Search by username or in-game name'}
                  value={sendPoints.searchTerm}
                  onChange={handleSearchChange}
                  className="role-search-input"
                />
                {sendPoints.isSearching && (
                  <div className="role-search-spinner">
                    <div className="search-spinner"></div>
                  </div>
                )}
              </div>
            </div>

            {/* Search Results */}
            {sendPoints.searchResults.length > 0 && (
              <div className="role-search-results">
                <div className="role-search-results-header">
                  <p className="role-search-results-title">{t('searchResults', language) || 'Search Results'} ({sendPoints.searchResults.length})</p>
                </div>
                {sendPoints.searchResults.map(resultUser => (
                  <div
                    key={resultUser._id}
                    onClick={() => selectUser(resultUser)}
                    className="role-search-result-item"
                  >
                    <div className="role-search-result-content">
                      <div className="role-search-result-info">
                        <p className="role-search-result-name">{resultUser.inGameName}</p>
                        <p className="role-search-result-email">{resultUser.email}</p>
                        <p className="role-search-result-stats">{resultUser.points} {t('points', language) || 'pts'} • {resultUser.wins}W-{resultUser.losses}L</p>
                      </div>
                      <div className="points-display">
                        <span className="points-badge">{resultUser.points}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Send Points Section */}
          <div className="role-selection-section">
            {sendPoints.selectedUser ? (
              <>
                <div className="selected-user-card">
                  <h5 className="selected-user-title">{t('recipient', language) || 'Recipient'}</h5>
                  <div className="selected-user-info">
                    <div className="selected-user-info-item">
                      <span className="selected-user-info-label">{t('username', language) || 'Username'}:</span>
                      <span className="selected-user-info-value">{sendPoints.selectedUser.username}</span>
                    </div>
                    <div className="selected-user-info-item">
                      <span className="selected-user-info-label">{t('inGameName', language) || 'In-Game Name'}:</span>
                      <span className="selected-user-info-value">{sendPoints.selectedUser.inGameName}</span>
                    </div>
                    <div className="selected-user-info-item">
                      <span className="selected-user-info-label">{t('theirPoints', language) || 'Their Points'}:</span>
                      <span className="selected-user-info-value points">{sendPoints.selectedUser.points}</span>
                    </div>
                  </div>
                </div>

                <div className="points-action-grid">
                  <div className="role-select-group">
                    <label className="role-select-label">
                      {t('amountToSend', language) || 'Amount to Send'}
                    </label>
                    <input
                      type="number"
                      min="1"
                      max={user.points}
                      placeholder={t('enterPointsAmount', language) || 'Enter points amount'}
                      value={sendPoints.pointsAmount}
                      onChange={(e) => setSendPoints(prev => ({ ...prev, pointsAmount: e.target.value }))}
                      className="role-search-input"
                    />
                  </div>

                  <div className="role-reason-group">
                    <label className="role-reason-label">
                      {t('reasonOptional', language) || 'Reason (Optional)'}
                    </label>
                    <textarea
                      value={sendPoints.reason}
                      onChange={(e) => setSendPoints(prev => ({ ...prev, reason: e.target.value }))}
                      placeholder={t('enterReasonForTransfer', language) || 'Enter reason for transfer'}
                      className="role-reason-textarea"
                      rows={2}
                    />
                  </div>

                  {sendPoints.pointsAmount && parseInt(sendPoints.pointsAmount) > 0 && (
                    <div className="points-preview">
                      <div className="role-change-preview">
                        <p className="role-change-preview-text">
                          <strong>{t('sending', language) || 'Sending'}:</strong> {sendPoints.pointsAmount} {t('points', language) || 'points'}
                        </p>
                        <p className="role-change-preview-description">
                          {t('yourNewBalance', language) || 'Your new balance'}: {newBalance >= 0 ? newBalance : 0} {t('points', language) || 'points'}
                          {newBalance < 0 && (
                            <span style={{ color: '#ef4444', marginLeft: '8px' }}>
                              ({t('insufficientFunds', language) || 'Insufficient funds'})
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={openConfirmModal}
                  disabled={loading || !sendPoints.pointsAmount || parseInt(sendPoints.pointsAmount) <= 0 || parseInt(sendPoints.pointsAmount) > user.points}
                  className="role-change-btn"
                  style={{
                    background: parseInt(sendPoints.pointsAmount) > user.points ? '#6b7280' : undefined,
                    cursor: parseInt(sendPoints.pointsAmount) > user.points ? 'not-allowed' : undefined
                  }}
                >
                  <Send className="w-4 h-4" style={{ marginRight: '8px' }} />
                  {loading ? (t('processing', language) || 'Processing...') : (t('sendPoints', language) || 'Send Points')}
                </button>
              </>
            ) : (
              <div className="role-placeholder">
                <Send className="role-placeholder-icon" />
                <p className="role-placeholder-text">{t('searchAndSelectRecipient', language) || 'Search and select a recipient to send points'}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Send Points Confirmation Modal */}
      {sendPoints.showConfirmModal && (
        <SendPointsConfirmationModal
          sendPoints={sendPoints}
          setSendPoints={setSendPoints}
          handleSendPoints={handleSendPoints}
          loading={loading}
          language={language}
          t={t}
          senderPoints={user.points}
        />
      )}
    </div>
  );
};

// Send Points Confirmation Modal
const SendPointsConfirmationModal = ({
  sendPoints,
  setSendPoints,
  handleSendPoints,
  loading,
  language,
  t,
  senderPoints
}) => {
  const confirmAction = () => {
    handleSendPoints();
  };

  const closeModal = () => {
    setSendPoints(prev => ({ ...prev, showConfirmModal: false }));
  };

  const pointsToSend = parseInt(sendPoints.pointsAmount);
  const newSenderBalance = senderPoints - pointsToSend;
  const newRecipientBalance = sendPoints.selectedUser.points + pointsToSend;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <div className="modal-icon" style={{ background: '#10b981' }}>
            <Send className="w-6 h-6 text-white" />
          </div>
          <h3 className="modal-title">{t('confirmTransfer', language) || 'Confirm Transfer'}</h3>
        </div>

        <div className="modal-body">
          <div className="modal-user-info">
            <p className="modal-user-name">{t('sendingTo', language) || 'Sending to'}: {sendPoints.selectedUser.inGameName}</p>
            <p className="modal-user-email">{sendPoints.selectedUser.email}</p>
          </div>

          <div className="modal-role-change">
            <div className="points-badge current">
              {senderPoints} {t('pts', language) || 'pts'}
            </div>
            <div className="modal-role-arrow">→</div>
            <div className="points-badge remove">
              {newSenderBalance} {t('pts', language) || 'pts'}
            </div>
          </div>

          <div className="modal-points-action">
            <p className="modal-reason-title">{t('transferDetails', language) || 'Transfer Details'}</p>
            <p className="modal-reason-text">
              {t('sendingAmount', language) || 'Sending'} <strong>{pointsToSend}</strong> {t('pointsTo', language) || 'points to'} <strong>{sendPoints.selectedUser.inGameName}</strong>
            </p>
            <p className="modal-reason-text" style={{ marginTop: '8px', color: '#10b981' }}>
              {t('theirNewBalance', language) || 'Their new balance'}: {newRecipientBalance} {t('points', language) || 'points'}
            </p>
          </div>

          {sendPoints.reason && (
            <div className="modal-reason">
              <p className="modal-reason-title">{t('reason', language) || 'Reason'}:</p>
              <p className="modal-reason-text">{sendPoints.reason}</p>
            </div>
          )}

          <div className="modal-warning">
            <p className="modal-warning-text">
              {t('transferWarning', language) || 'This transfer is immediate and cannot be undone. The points will be deducted from your wallet.'}
            </p>
          </div>
        </div>

        <div className="modal-actions">
          <button
            onClick={confirmAction}
            disabled={loading}
            className="modal-btn modal-btn-primary"
            style={{ background: '#10b981' }}
          >
            <Send className="w-4 h-4" style={{ marginRight: '8px' }} />
            {loading ? (t('processing', language) || 'Processing...') : (t('confirmSend', language) || 'Confirm Send')}
          </button>
          <button
            onClick={closeModal}
            disabled={loading}
            className="modal-btn modal-btn-secondary"
          >
            {t('cancel', language) || 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendPointsTab;
